import { Icons } from '@/assets/icons/icons';

import type { ComponentType } from 'react';

type FeedKind = {
  icon: ComponentType<{ className?: string }>;
};

const KINDS = {
  reply: { icon: Icons.reply },
  message: { icon: Icons.newChat },
  email: { icon: Icons.mail },
  invite: { icon: Icons.calendar },
  other: { icon: Icons.newChat },
} satisfies Record<string, FeedKind>;

const kindOf = (lowered: string): FeedKind => {
  if (lowered.includes('reply')) return KINDS.reply;
  if (lowered.includes('message')) return KINDS.message;
  if (lowered.includes('email')) return KINDS.email;
  if (/rsvp|invite|event|meeting/.test(lowered)) return KINDS.invite;
  return KINDS.other;
};

export const capitalized = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

const draftOrReply = (isReply: boolean, draft: string) =>
  isReply ? 'Reply drafted' : draft;

const DONE_FOR_YOU: Record<
  string,
  string | ((content: Record<string, unknown>) => string)
> = {
  gmail_email_draft: (content) =>
    draftOrReply(!!content.thread_id, 'Email drafted'),
  gmail_email_draft_with_attachments: (content) =>
    draftOrReply(!!content.thread_id, 'Email drafted'),
  outlook_email_draft: (content) =>
    draftOrReply(!!content.reply_to_message_id, 'Email drafted'),
  slack_message: (content) =>
    draftOrReply(!!content.thread_ts, 'Message drafted'),
  slack_message_with_attachments: (content) =>
    draftOrReply(!!content.thread_ts, 'Message drafted'),
  slack_reaction: 'Suggested',
  teammate_message: (content) =>
    draftOrReply(!!content.incoming_text, 'Message drafted'),
  google_calendar_event: 'Event drafted',
  google_calendar_rsvp: 'Suggested',
  google_calendar_event_update: 'Suggested',
  todoist_task: 'Task drafted',
  todoist_task_update: 'Edits drafted',
  todoist_task_complete: 'Suggested',
  todoist_task_uncomplete: 'Suggested',
  todoist_object_delete: 'Suggested',
  google_task: 'Task drafted',
  google_task_update: 'Edits drafted',
  google_task_delete: 'Suggested',
  google_task_move: 'Suggested',
  github_pull_request: 'PR drafted',
  github_pull_request_update: 'Edits drafted',
  github_pull_request_close: 'Suggested',
  github_pull_request_merge: 'Suggested',
  github_pull_request_approve: 'Suggested',
  github_pull_request_request_changes: 'Review drafted',
  github_pull_request_review_comment: 'Comment drafted',
  github_comment: 'Comment drafted',
  linear_issue: 'Issue drafted',
  linear_issue_update: 'Edits drafted',
  linear_comment: 'Comment drafted',
  notion_page: 'Page drafted',
  notion_page_update: 'Edits drafted',
  notion_comment: (content) =>
    content.operation === 'update' ? 'Edits drafted' : 'Comment drafted',
  notion_comment_action: 'Suggested',
  notion_page_move: 'Suggested',
  notion_page_duplicate: 'Suggested',
  notion_trash: 'Suggested',
  notion_database_change: 'Suggested',
  hubspot_contact_create: 'Contact drafted',
  hubspot_contact_update: 'Edits drafted',
  hubspot_contact_note: 'Note drafted',
  hubspot_company: 'Company drafted',
  hubspot_deal: 'Deal drafted',
  hubspot_deal_update: 'Edits drafted',
  hubspot_task: 'Task drafted',
  hubspot_task_update: 'Edits drafted',
};

export const feedDoneForYou = (
  actionType: string,
  content: Record<string, unknown>
): string | undefined => {
  const label = DONE_FOR_YOU[actionType];
  return typeof label === 'function' ? label(content) : label;
};

export const TODO_KIND = {
  label: 'To-do',
  icon: Icons.flag,
};

export const feedLabelOf = (
  todo: boolean,
  verb: string | null,
  doneForYou: string | undefined
): string => (todo ? TODO_KIND.label : (doneForYou ?? feedKindOf(verb).label));

export const feedKindOf = (verb: string | null) => {
  if (!verb) return { label: 'Update', icon: Icons.info };
  return {
    ...kindOf(verb.toLowerCase()),
    label: capitalized(verb).replace(/\bnew\s+/i, ''),
  };
};
